import React from "react"

import {
  trustpilotIcon,
  appStoreIcon,
  googlePlayIcon,
  facebookIcon,
  instagramIcon,
  linkedinIcon,
  starsRating,
  playIcon
} from "../assets"

import { useVideoPlayer } from "./VideoPlayerContext"

const StarRow = ({ rating = 5 }) => (
  <div className="pro-review-stars">
    {Array.from({ length: 5 }, (_, i) => (
      <svg key={i} className="pro-review-star" viewBox="0 0 16 16" fill={i < rating ? "#FFB800" : "none"}>
        <path
          d="M8 1.5L9.5 5.5L13.5 6L10.5 8.5L11.5 12.5L8 10.5L4.5 12.5L5.5 8.5L2.5 6L6.5 5.5L8 1.5Z"
          fill={i < rating ? "#FFB800" : "none"}
          stroke={i < rating ? "#FFB800" : "#FFB800"}
          strokeWidth="1"
        />
      </svg>
    ))}
  </div>
)

const ReviewCard = ({ review, index }) => {
  const { source, rating, title, body, authorName, profession, locationAndDate, avatarUrl, imageUrl, videoUrl } = review
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [isMuted, setIsMuted] = React.useState(false)
  const [videoPreview, setVideoPreview] = React.useState(imageUrl)
  const videoRef = React.useRef(null)
  const vimeoPlayerRef = React.useRef(null)
  const iframeRef = React.useRef(null)

  // Generate unique video ID for this card
  const videoId = `video-${index}-${authorName?.replace(/\s+/g, "-").toLowerCase() || "unknown"}`

  // Use video player context
  const { registerVideo, unregisterVideo, pauseAllOtherVideos } = useVideoPlayer()

  // Check if videoUrl is a Vimeo URL
  const isVimeoUrl = videoUrl && (videoUrl.includes("vimeo.com") || videoUrl.includes("player.vimeo.com"))

  // Extract Vimeo video ID for embed
  const getVimeoEmbedUrl = (url) => {
    if (url.includes("vimeo.com/")) {
      const videoId = url.split("vimeo.com/")[1].split("?")[0]
      return `https://player.vimeo.com/video/${videoId}?h=auto&autoplay=0&muted=0&controls=0&dnt=1`
    }
    return url
  }

  // Initialize Vimeo Player API when iframe is ready
  React.useEffect(() => {
    if (isVimeoUrl && iframeRef.current) {
      // Load Vimeo Player API script
      const script = document.createElement("script")
      script.src = "https://player.vimeo.com/api/player.js"
      script.onload = () => {
        if (iframeRef.current && window.Vimeo) {
          vimeoPlayerRef.current = new window.Vimeo.Player(iframeRef.current)

          // Listen to player events to sync our state
          vimeoPlayerRef.current.on("play", () => {
            setIsPlaying(true)
          })
          vimeoPlayerRef.current.on("pause", () => {
            setIsPlaying(false)
          })
          vimeoPlayerRef.current.on("ended", () => {
            setIsPlaying(false)
          })
          vimeoPlayerRef.current.on("loaded", () => {
            // Set initial unmuted state for Vimeo
            vimeoPlayerRef.current.setMuted(false)
            setIsMuted(false)

            // Register this Vimeo player with the context
            registerVideo(videoId, {
              vimeoPlayer: vimeoPlayerRef.current,
              pauseFn: () => {
                if (vimeoPlayerRef.current) {
                  vimeoPlayerRef.current.pause()
                  setIsPlaying(false)
                }
              }
            })
          })
        }
      }
      document.head.appendChild(script)

      return () => {
        if (vimeoPlayerRef.current) {
          vimeoPlayerRef.current.destroy()
        }
      }
    }
  }, [isVimeoUrl])

  // Set video preview - use imageUrl if provided, otherwise use default
  React.useEffect(() => {
    if (source === "video" && !isVimeoUrl) {
      // Use provided imageUrl or fallback to default video preview
      setVideoPreview(
        imageUrl ||
          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjgyIiBoZWlnaHQ9IjQ0MCIgdmlld0JveD0iMCAwIDI4MiA0NDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyODIiIGhlaWdodD0iNDQwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xNDEgMjIwTDEwMSAxOTBMMTQxIDE2MFYyMjBaIiBmaWxsPSIjQ0NDIi8+Cjwvc3ZnPgo="
      )
    }
  }, [source, imageUrl, isVimeoUrl])

  // Register MP4 video player when videoRef is available
  React.useEffect(() => {
    if (!isVimeoUrl && videoRef.current && source === "video") {
      registerVideo(videoId, {
        videoElement: videoRef.current,
        pauseFn: () => {
          if (videoRef.current) {
            videoRef.current.pause()
            setIsPlaying(false)
          }
        }
      })
    }
  }, [isVimeoUrl, source, registerVideo, videoId])

  // Cleanup when component unmounts
  React.useEffect(() => () => unregisterVideo(videoId), [unregisterVideo, videoId])

  const handleVideoClick = () => {
    if (isVimeoUrl && vimeoPlayerRef.current) {
      // For Vimeo, use our custom controls
      try {
        if (isPlaying) {
          vimeoPlayerRef.current.pause()
        } else {
          // Pause all other videos before playing this one
          pauseAllOtherVideos(videoId)
          vimeoPlayerRef.current.play()
        }
      } catch (error) {
        // Silently handle Vimeo player errors
      }
      return
    }

    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
        setIsPlaying(false)
      } else {
        // Pause all other videos before playing this one
        pauseAllOtherVideos(videoId)
        videoRef.current.play()
        setIsPlaying(true)
      }
    }
  }

  const handleVideoEnded = () => {
    setIsPlaying(false)
  }

  const handleMuteToggle = (e) => {
    e.stopPropagation() // Prevent triggering video play/pause

    if (isVimeoUrl && vimeoPlayerRef.current) {
      // For Vimeo, use our custom controls
      const newMutedState = !isMuted
      vimeoPlayerRef.current.setMuted(newMutedState)
      setIsMuted(newMutedState)
      return
    }

    if (videoRef.current && !isVimeoUrl) {
      const newMutedState = !isMuted
      videoRef.current.muted = newMutedState
      setIsMuted(newMutedState)
    }
  }

  // Video review card - updated to handle both Vimeo and direct video files
  if (source === "video") {
    return (
      <div
        className="pro-video-card"
        style={{
          backgroundImage: isPlaying || isVimeoUrl ? "none" : `url(${videoPreview})`,
          cursor: "pointer",
          position: "relative" // Ensure proper positioning for absolute children
        }}
        onClick={handleVideoClick}
        title={isPlaying ? "Click to pause" : "Click to play"}>
        {/*
          The entire card area is clickable for both Vimeo and MP4 videos.
          - For Vimeo: iframe has pointerEvents: "none" so clicks go through to the card
          - For MP4: direct video element with custom controls
        */}

        {/* Vimeo iframe embed */}
        {isVimeoUrl && (
          <iframe
            ref={iframeRef}
            src={getVimeoEmbedUrl(videoUrl)}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
              pointerEvents: "none" // Prevent iframe from capturing clicks
            }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={`${authorName} - ${profession}`}
          />
        )}

        {/* HTML5 video element - only for direct video files */}
        {!isVimeoUrl && (
          <video
            ref={videoRef}
            src={videoUrl}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: isPlaying ? "block" : "none"
            }}
            onEnded={handleVideoEnded}
            muted={isMuted}
            preload="metadata"
          />
        )}

        {/* Play/Pause button in center - always show for Vimeo, show play for MP4 when not playing */}
        {((isVimeoUrl && !isPlaying) || (!isVimeoUrl && !isPlaying)) && (
          <div className="pro-video-play">
            <img src={playIcon} alt="Play" width="64" height="65" />
          </div>
        )}

        {/* Pause button for Vimeo when playing */}
        {isVimeoUrl && isPlaying && (
          <div className="pro-video-pause">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" fill="white" />
            </svg>
          </div>
        )}

        {/* Mute/Unmute button - show for both Vimeo and direct videos when playing */}
        {isPlaying && (
          <button className="pro-video-mute-button" onClick={handleMuteToggle} title={isMuted ? "Unmute" : "Mute"}>
            {isMuted ? (
              // Muted icon (speaker with slash)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M11 5L6 9H2V15H6L11 19V5Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line x1="1" y1="1" x2="23" y2="23" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              // Unmuted icon (speaker with sound waves)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M11 5L6 9H2V15H6L11 19V5Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.54 8.46C16.4774 9.39764 17.0039 10.6696 17.0039 12C17.0039 13.3304 16.4774 14.6024 15.54 15.54"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19.07 4.93C20.9447 6.80528 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}

        {/* Gradient overlay at bottom */}
        <div className="pro-video-overlay">
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <StarRow rating={rating} />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <div
                style={{
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "bold",
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "white"
                }}>
                {authorName}
              </div>
              <div
                style={{
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "normal",
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#d9d9d9"
                }}>
                {profession}
              </div>
              <div
                style={{
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "normal",
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#d9d9d9"
                }}>
                {locationAndDate}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Regular review card - matches Figma design
  return (
    <div className="pro-review-card">
      <div className="pro-review-header">
        <div
          className="pro-review-avatar"
          style={{
            backgroundImage: avatarUrl ? `url(${avatarUrl})` : "none",
            backgroundColor: avatarUrl ? "transparent" : "#e5e7eb"
          }}>
          {!avatarUrl && (
            <div className="pro-review-avatar-placeholder">
              {authorName
                ? authorName
                    .split(" ")
                    .map((name) => name.charAt(0))
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)
                : "?"}
            </div>
          )}
        </div>
        <div className="pro-review-info">
          <div className="pro-review-name">{authorName}</div>
          <div className="pro-review-profession">{profession}</div>
        </div>
        <div className="pro-review-platform">
          {source === "trustpilot" && <img src={trustpilotIcon} alt="Trustpilot" width="24" height="24" />}
          {source === "app_store" && <img src={appStoreIcon} alt="App Store" width="24" height="24" />}
          {source === "google_play" && <img src={googlePlayIcon} alt="Google Play" width="24" height="24" />}
          {source === "facebook" && <img src={facebookIcon} alt="Facebook" width="24" height="24" />}
          {source === "instagram" && <img src={instagramIcon} alt="Instagram" width="24" height="24" />}
          {source === "linkedin" && <img src={linkedinIcon} alt="LinkedIn" width="24" height="24" />}
        </div>
      </div>

      <div className="pro-review-body">
        {/* Trustpilot has stars rating image before text */}
        {source === "trustpilot" && (
          <div className="pro-review-trustpilot-stars">
            <img src={starsRating} alt="Stars Rating" width="100" height="18" />
          </div>
        )}

        {/* Show star rating for other platforms that have ratings */}
        {rating && source !== "trustpilot" && <StarRow rating={rating} />}

        {title && <div className="pro-review-title">{title}</div>}
        {body && <div className="pro-review-text">{body}</div>}
      </div>

      <div className="pro-review-footer" style={{ paddingBottom: "16px" }}>
        <div className="pro-review-location">{locationAndDate}</div>
      </div>
    </div>
  )
}

export default ReviewCard
